import React, { useCallback, useEffect, useState } from "react";
import { FileText, Scale, Landmark, BookOpenCheck, Download } from "lucide-react";
import { fetchStatements, fetchDepreciation, pdfExportUrl } from "../../api";
import { AjeTab } from "./AjeTab";

const money = (n) =>
  `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const Section = ({ title, lines, total, totalLabel }) => (
  <div className="space-y-1">
    <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 pt-3">{title}</div>
    {lines.map((l) => (
      <div key={l.code} className="flex justify-between items-baseline text-xs py-1 border-b border-white/5">
        <span className="text-zinc-300">
          <span className="font-mono text-zinc-500 mr-2">{l.code}</span>
          {l.name}
          {l.tax_line && <span className="ml-2 text-[10px] text-blue-400 font-mono">{l.tax_line}</span>}
        </span>
        <span className="font-mono font-semibold text-zinc-200">{money(l.amount)}</span>
      </div>
    ))}
    <div className="flex justify-between text-xs py-1.5 font-bold text-white">
      <span>{totalLabel}</span>
      <span className="font-mono">{money(total)}</span>
    </div>
  </div>
);

const PLTab = ({ pl }) => (
  <div className="glass-card rounded-md border border-white/10 p-6 space-y-2" data-testid="pl-statement">
    <div className="flex items-center justify-between border-b border-white/10 pb-3">
      <div>
        <h3 className="text-sm font-bold text-white">Profit and Loss ({pl.basis} basis)</h3>
        <p className="text-[11px] text-zinc-400">{pl.period}</p>
      </div>
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${pl.foots ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" : "bg-rose-500/10 border-rose-500/30 text-rose-400"}`}>
        {pl.foots ? "Foots \u2713" : "DOES NOT FOOT"}
      </span>
    </div>
    <Section title="Revenue" lines={pl.revenue} total={pl.total_revenue} totalLabel="Total revenue" />
    {pl.cogs.length > 0 && (
      <>
        <Section title="Cost of Revenue" lines={pl.cogs} total={pl.total_cogs} totalLabel="Total cost of revenue" />
        <div className="flex justify-between text-xs py-1.5 font-bold text-white bg-white/[0.04] px-2 rounded">
          <span>GROSS PROFIT ({pl.gross_margin_pct}%)</span>
          <span className="font-mono">{money(pl.gross_profit)}</span>
        </div>
      </>
    )}
    <Section title="Operating Expenses" lines={pl.opex} total={pl.total_opex} totalLabel="Total operating expenses" />
    <div className="flex justify-between items-center text-sm py-3 border-t-2 border-white/60 font-black text-white">
      <span>NET INCOME ({pl.net_margin_pct}% margin)</span>
      <span className={`font-mono ${pl.net_income < 0 ? "text-rose-400" : "text-emerald-400"}`} data-testid="pl-net-income">
        {money(pl.net_income)}
      </span>
    </div>
    {pl.reversed_sign_lines.length > 0 && (
      <div className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md p-2">
        Reversed-sign expense lines: {pl.reversed_sign_lines.join("; ")}
      </div>
    )}
  </div>
);

const M1Tab = ({ m1 }) => (
  <div className="glass-card rounded-md border border-white/10 p-6 space-y-3" data-testid="m1-statement">
    <h3 className="text-sm font-bold text-white border-b border-white/10 pb-3">
      Schedule M-1 — Book to Tax Reconciliation
    </h3>
    <div className="flex justify-between text-xs py-1.5 font-semibold text-zinc-200">
      <span>Net income per books</span>
      <span className="font-mono">{money(m1.book_income)}</span>
    </div>
    {m1.permanent_differences.length > 0 && (
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">Permanent differences (add-backs)</div>
        {m1.permanent_differences.map((d) => (
          <div key={d.label} className="flex justify-between text-xs py-1 border-b border-white/5 pl-3">
            <span className="text-zinc-400">{d.label}</span>
            <span className="font-mono font-semibold text-amber-400">{money(d.amount)}</span>
          </div>
        ))}
      </div>
    )}
    {m1.timing_differences.length > 0 && (
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">Timing differences</div>
        {m1.timing_differences.map((d) => (
          <div key={d.label} className="flex justify-between text-xs py-1 border-b border-white/5 pl-3">
            <span className="text-zinc-400">{d.label}</span>
            <span className="font-mono font-semibold text-zinc-300">{money(d.amount)}</span>
          </div>
        ))}
      </div>
    )}
    <div className="flex justify-between text-sm py-3 border-t-2 border-white/60 font-black text-white">
      <span>TAXABLE INCOME</span>
      <span className="font-mono" data-testid="m1-taxable-income">{money(m1.taxable_income)}</span>
    </div>
    <p className="text-[11px] text-zinc-400">
      Non-deductible and partially deductible expenses (e.g. 50% meals limitation) are added back to book income —
      add-backs increase taxable income.
    </p>
  </div>
);

const DepreciationTab = ({ dep }) => (
  <div className="glass-card rounded-md border border-white/10 p-6 space-y-4" data-testid="depreciation-schedule">
    <div className="flex items-center justify-between border-b border-white/10 pb-3">
      <h3 className="text-sm font-bold text-white">Form 4562 — Depreciation Schedule (Tax Year {dep.tax_year})</h3>
      {dep.assets.length > 0 && (
        <span className="text-xs font-black font-mono text-white" data-testid="depreciation-total">
          Total: {money(dep.total)}
        </span>
      )}
    </div>
    {dep.assets.length === 0 ? (
      <p className="text-xs text-zinc-400" data-testid="depreciation-empty">{dep.message}</p>
    ) : (
      <>
        <table className="w-full text-left text-[11px]">
          <thead className="text-zinc-400 uppercase tracking-wider border-b border-white/10">
            <tr>
              <th className="py-2">Asset</th>
              <th className="py-2">In Service</th>
              <th className="py-2 text-center">Life</th>
              <th className="py-2 text-center">Convention</th>
              <th className="py-2 text-right">Basis</th>
              <th className="py-2 text-right">§179</th>
              <th className="py-2 text-right">Bonus §168(k)</th>
              <th className="py-2 text-right">MACRS</th>
              <th className="py-2 text-right">Total Yr 1</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 text-zinc-300">
            {dep.assets.map((a) => (
              <tr key={a.transaction_id}>
                <td className="py-2 font-semibold">{a.description}</td>
                <td className="py-2 font-mono text-zinc-400">{a.in_service_date}</td>
                <td className="py-2 text-center font-mono">{a.class_life}-yr</td>
                <td className="py-2 text-center text-zinc-400">{a.convention.replace(/_/g, " ")}</td>
                <td className="py-2 text-right font-mono">{money(a.basis)}</td>
                <td className="py-2 text-right font-mono text-purple-300">{money(a.section_179)}</td>
                <td className="py-2 text-right font-mono text-blue-300">{money(a.bonus)}</td>
                <td className="py-2 text-right font-mono">{money(a.macrs)}</td>
                <td className="py-2 text-right font-mono font-bold">{money(a.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="space-y-1 text-[11px] text-zinc-400">
          {dep.section_179_limited && (
            <p className="text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md p-2" data-testid="s179-limited-note">
              ** Section 179 limited (annual dollar cap and/or taxable-income limitation applied)
            </p>
          )}
          {dep.mid_quarter_triggered && (
            <p className="text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md p-2">
              ** Mid-quarter convention triggered (&gt;40% of basis placed in service in Q4)
            </p>
          )}
          <p>
            Ordering: Section 179 (cap → phase-out → taxable-income limit), then bonus depreciation on remaining basis,
            then MACRS on the remainder. Depreciation flows to the P&amp;L through an approved adjusting entry.
          </p>
        </div>
      </>
    )}
  </div>
);

const TABS = [
  { id: "pl", label: "Profit & Loss", icon: FileText },
  { id: "m1", label: "Schedule M-1", icon: Scale },
  { id: "depreciation", label: "Depreciation (4562)", icon: Landmark },
  { id: "ajes", label: "Adjusting Entries", icon: BookOpenCheck },
];

const ReportsView = ({ initialTab, coaOptions }) => {
  const [tab, setTab] = useState(initialTab || "pl");
  const [statements, setStatements] = useState(null);
  const [dep, setDep] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    Promise.all([fetchStatements(), fetchDepreciation()])
      .then(([s, d]) => {
        setStatements(s);
        setDep(d);
      })
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-rose-400" data-testid="view-reports">
        Failed to load statements from the engine.
      </div>
    );
  if (!statements || !dep)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-zinc-400 animate-pulse" data-testid="view-reports">
        Generating deliverables…
      </div>
    );

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar p-8" data-testid="view-reports">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="border-b border-white/10 pb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white">Deliverable Reports</h1>
            <p className="text-zinc-400 text-xs mt-1">
              Generated live from the classified transaction population — reconciled to the trial balance.
            </p>
          </div>
          <a
            data-testid="download-pdf-btn"
            href={pdfExportUrl()}
            download="PnLFlow_Deliverable_Packet.pdf"
            className="px-3.5 py-2 bg-white hover:bg-zinc-200 text-black text-xs font-semibold rounded-md transition flex items-center space-x-1.5"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Download PDF Packet</span>
          </a>
        </div>

        <div className="flex items-center space-x-1 bg-white/5 p-1 rounded-md border border-white/10 text-xs font-medium w-fit">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`reports-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 rounded-md transition flex items-center space-x-1.5 ${
                  tab === t.id ? "bg-blue-600 text-white font-semibold shadow-sm" : "text-zinc-400 hover:text-white"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        {tab === "pl" && <PLTab pl={statements.pl} />}
        {tab === "m1" && <M1Tab m1={statements.m1} />}
        {tab === "depreciation" && <DepreciationTab dep={dep} />}
        {tab === "ajes" && <AjeTab coaOptions={coaOptions || []} onPosted={load} />}
      </div>
    </div>
  );
};

export default ReportsView;
